import React, { Children, useId, useMemo, useRef, useState } from 'react';
import {
  DayPicker,
  useDayPicker,
  type DateRange,
  type DropdownNavProps,
  type DropdownProps,
  type Matcher,
} from 'react-day-picker';

import { useTranslation } from '@mint-lib/i18n';
import { addYears, endOfYear, format, getMonth } from 'date-fns';

import { ClickAwayListener, Popper, type ValueTypes } from '@myn/mui';

import CalendarButton from '../../common/Calendar/CalendarButton.jsx';
import CalendarNavigation from '../../common/Calendar/CalendarNavigation.jsx';
import { StyledDayPickerWrapper } from '../../common/Calendar/DayPickerWrapper.jsx';
import { useDateLocalize } from '../../hooks/useDateLocalize.js';
import {
  getDateRangeBySelectButton,
  selectButtons,
  type SelectButtonType,
} from '../../utils/getDateRangeBySelectButton.js';
import Box from '../Box/Box.jsx';
import Button from '../Button/Button.jsx';
import IconButton from '../IconButton/IconButton.jsx';
import Input from '../Input/Input.jsx';
import Select from '../Select/Select.jsx';
import ToggleButton from '../ToggleButton/ToggleButton.jsx';
import type { TooltipProps } from '../Tooltip/Tooltip.jsx';
import Typography from '../Typography/Typography.jsx';

export interface DatePickerRangeProps {
  /**
   * PROPS FOR THE INPUT
   */

  /**
   * Color of the datepicker input
   */
  color?: 'primary' | 'secondary';
  /**
   * Label for the datepicker input
   */
  label?: string;
  /**
   * If true, the datepicker input will be full width.
   * @default false
   */
  fullWidth?: boolean;
  /**
   * The helper text of the datepicker input
   */
  helperText?: string;
  /**
   * If true, the datepicker input will be in error status
   */
  error?: boolean;
  /**
   * If true, the datepicker input will be in warning status
   */
  warning?: boolean;
  /**
   * The size of the datepicker input
   */
  size?: 'small' | 'medium';
  /**
   * The info text of the datepicker input
   */
  infoText?: string;
  /**
   * If true, the datepicker input will be disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * If true, the datepicker input will be readOnly
   * @default false
   */
  /**
   * If true, the datepicker input will be readOnly
   */
  readOnly?: boolean;
  /**
   * Props for the info text tooltip
   */
  InfoTextTooltipProps?: Partial<TooltipProps>;
  /**
   * onFocus event handler for the input
   * @description Use `onApply` instead
   */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * onBlur event handler for the input
   * @description Use `onCancel` instead
   */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;

  /**
   * Prop that turn off 'close' icon button in input.
   * @default true
   */
  clearable?: boolean;
  /**
   * Callback that adds extra logic to `close` icon button in input.
   */
  onClear?: () => void;
  /**
   * PROPS FOR THE CALENDAR
   */
  /**
   * Class name for the datepicker
   */
  className?: string;
  /**
   * Callback that applies the selected date range and sidebar selected button. It will be called when the `Apply` button is clicked.
   */
  onApply?: (
    dateRange?: { from?: Date | null; to?: Date | null },
    selectedButton?: SelectButtonType,
  ) => void;

  /**
   * If true, the datepicker will be open
   * @default false
   */
  isOpen?: boolean;

  /**
   * Object with selected dates: from and to
   */
  selected: {
    from?: Date | null;
    to?: Date | null;
  };

  /**
   * If true, the datepicker will have a sidebar with predefined date ranges
   */
  sidebar?: boolean;
  /**
   * All time button configuration. For using need to pass from and to dates
   */
  allTimeButton?: {
    from?: Date;
    to?: Date;
  };
  /**
   * Prop for disabling all time option
   */
  disableAllTimeButton?: boolean;
  /**
   * Event dates to be highlighted in the calendar
   */
  eventDates?: Date[];
  /**
   * The position of the calendar dropdown
   */
  placement?:
    | 'bottom-start'
    | 'bottom-end'
    | 'top-start'
    | 'top-end'
    | 'right-start'
    | 'right-end'
    | 'left-start'
    | 'left-end';
  /**
   * Helps to disable dates in various ways
   */
  disabledDates?: Matcher;

  /**
   * Props for the popper component that helps to control the position of the calendar (also works with Modal Dialog)
   **/
  disablePortal?: boolean;
  /** Callback that fires if user closed the datepicker without applying the selected date
   **/
  onCancel?: () => void;
  /**
   * The selected sidebar button, if it was saved beforehand
   */
  selectedButton?: SelectButtonType;
}

/**
 * @deprecated Use DatepickerInput and CalendarRange from @mint-lib/tw-components/patterns instead
 */
const DatePickerRange = ({
  placement = 'bottom-start',
  disableAllTimeButton = false,
  clearable = true,
  ...props
}: DatePickerRangeProps) => {
  const { t } = useTranslation('@myn/mui');

  const [localSelected, setLocalSelected] = useState<DateRange | undefined>({
    from: props.selected.from ?? undefined,
    to: props.selected.to ?? undefined,
  } satisfies DateRange);
  const popperMods = useMemo(
    () => [
      {
        name: 'flip',
        enabled: true,
        options: {
          altBoundary: false,
          rootBoundary: 'document',
        },
      },
      {
        name: 'preventOverflow',
        enabled: true,
        options: {
          altAxis: true,
          altBoundary: false,
          tether: false,
          rootBoundary: 'viewport',
        },
      },
    ],
    [],
  );
  const localize = useDateLocalize();

  const formattedDateFrom = useMemo(
    () =>
      localSelected?.from &&
      format(localSelected.from, 'P', {
        locale: localize,
      }),
    [localSelected, localize],
  );

  const formattedDateTo = useMemo(
    () =>
      localSelected?.to &&
      format(localSelected.to, 'P', {
        locale: localize,
      }),
    [localSelected, localize],
  );
  const initialDateValue = formattedDateFrom
    ? `${formattedDateFrom} - ${formattedDateTo}`
    : '';

  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(
    Boolean(props.isOpen),
  );

  const [outerInputValue, setOuterInputValue] =
    useState<string>(initialDateValue);
  const [selectMonth, setSelectMonth] = useState<Date | undefined>(
    props.selected.to ?? undefined,
  );
  const [selectedButton, setSelectedButton] = useState<
    SelectButtonType | undefined
  >(props.selectedButton);

  // handler for applying the selected date
  const handleDateApply = (date: string) => {
    setOuterInputValue(date);
    setIsCalendarOpen(false);
    props.onApply?.(
      { from: localSelected?.from, to: localSelected?.to },
      selectedButton,
    );
  };

  // handler for opening the calendar
  const handleOpen = () => {
    const from =
      outerInputValue !== ''
        ? (props.selected.from as DateRange['from'])
        : undefined;
    const to =
      outerInputValue !== ''
        ? (props.selected.to as DateRange['to'])
        : undefined;
    setIsCalendarOpen((prev) => !prev);
    setLocalSelected({ from, to });
  };

  // handle for canceling on click away
  const handleCancel = () => {
    setIsCalendarOpen(false);
    setSelectedButton(undefined);
    setLocalSelected({
      from: outerInputValue ? localSelected?.from : undefined,
      to: outerInputValue ? localSelected?.to : undefined,
    });
    props.onCancel?.();
  };

  // handler for clearing the selected date in the input
  const handleClear = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    setOuterInputValue('');
    setLocalSelected({
      from: undefined,
      to: undefined,
    });
    props.onClear?.();
  };

  // handler for resetting the selected date inside the calendar
  const handleReset = () => {
    setLocalSelected({
      from: undefined,
      to: undefined,
    });
    setSelectedButton(undefined);
  };

  //Function that help getting the enable month and check type of disableDates.
  //It is need for the defaultMonth prop in the DayPicker
  const getEnabledMonth = (disableDates?: Matcher) => {
    if (
      typeof disableDates === 'object' &&
      ('after' in disableDates || 'from' in disableDates)
    ) {
      const enableMonth =
        'from' in disableDates ? disableDates.from : disableDates.after;
      return enableMonth;
    }
    return new Date();
  };

  // anchor element for the popper
  const anchorEl = useRef(null);
  const popperId = useId();

  const buttonsLabels: Record<SelectButtonType, string> = {
    yesterday: t('Yesterday'),
    thisWeek: t('This week'),
    lastWeek: t('Last week'),
    thisMonth: t('This month'),
    lastMonth: t('Last month'),
    thisQuarter: t('This quarter'),
    lastQuarter: t('Last quarter'),
    thisYear: t('This Year'),
    lastYear: t('Last Year'),
    allTime: t('All time'),
  };

  return (
    <ClickAwayListener mouseEvent="onMouseDown" onClickAway={handleCancel}>
      <Box aria-describedby={popperId}>
        <CalendarButton
          variant="range"
          ref={anchorEl}
          onFocus={props.onFocus}
          onBlur={props.onBlur}
          value={outerInputValue}
          infoText={props.infoText}
          disabledTextfield={props.disabled}
          InfoTextTooltipProps={props.InfoTextTooltipProps}
          warning={props.warning}
          color={props.color}
          label={props.label}
          fullWidth={props.fullWidth}
          error={props.error}
          size={props.size}
          endAdornment={
            clearable && !props.readOnly && outerInputValue !== '' ? (
              <IconButton
                icon="Close"
                size="small"
                variant="icon"
                color="secondary"
                onClick={handleClear}
                data-testid="date-picker-clear-button"
              />
            ) : null
          }
          helperText={!isCalendarOpen ? props.helperText : undefined}
          readOnlyTextfield={props.readOnly}
          onInputClick={() => {
            if (props.readOnly || props.disabled) {
              return;
            }
            handleOpen();
          }}
        />

        <Popper
          id={popperId}
          open={isCalendarOpen}
          //@ts-ignore -> rethinking this logic
          anchorEl={() => anchorEl.current}
          placement={placement}
          disablePortal={props.disablePortal}
          modifiers={popperMods}
          sx={{
            zIndex: 2,
          }}
        >
          <StyledDayPickerWrapper variant="range" sidebar={props.sidebar}>
            {props.sidebar && (
              <Box
                sx={{
                  height: '336px',
                  padding: (theme) => `${theme.spacing(0)} ${theme.spacing(1)}`,
                  display: 'flex',
                  alignItems: 'start',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: '8px',
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.border.secondary}`,
                }}
              >
                {selectButtons.map((key) => {
                  if (key === 'allTime' && !props.allTimeButton) {
                    return null;
                  }
                  return (
                    <ToggleButton
                      key={key}
                      className="toggleButton"
                      size="small"
                      value={key}
                      label={buttonsLabels[key]}
                      onClick={() => {
                        const { from, to, selectMonthDate } =
                          getDateRangeBySelectButton(
                            key,
                            localize,
                            props.allTimeButton,
                          );
                        setLocalSelected({ from, to });
                        setSelectMonth(selectMonthDate);
                        setSelectedButton(key);
                      }}
                      selected={selectedButton === key}
                    />
                  );
                })}
              </Box>
            )}
            <DayPicker
              {...props}
              data-testid="date-picker-range-calendar"
              mode="range"
              month={selectMonth}
              onMonthChange={setSelectMonth}
              defaultMonth={getEnabledMonth(props.disabledDates)}
              numberOfMonths={2}
              locale={localize}
              endMonth={endOfYear(addYears(new Date(), 50))}
              captionLayout="dropdown"
              selected={localSelected}
              onSelect={(date, _triggerDate, _modifiers, e) => {
                setLocalSelected(date);
                setSelectedButton(undefined);
              }}
              formatters={{
                formatWeekdayName: (date) =>
                  format(date, 'ccccc', {
                    locale: localize,
                  }),
              }}
              disabled={props.disabledDates}
              modifiers={{
                eventDays: props.eventDates,
              }}
              modifiersClassNames={{
                eventDays: 'rdp-event-days_style',
              }}
              components={{
                Nav: CalendarNavigation,
                Dropdown: DropdownSelect,
                DropdownNav: DropdownNavigation,
              }}
              footer={
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderTop: (theme) =>
                      `1px solid ${theme.palette.border.secondary}`,

                    padding: '8px 12px',
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <Box sx={{ width: '112px' }}>
                      <Input
                        size="small"
                        placeholder={t('Start date')}
                        fullWidth
                        readOnly
                        value={formattedDateFrom ?? ''}
                      />
                    </Box>
                    <Typography> - </Typography>
                    <Box sx={{ width: '112px' }}>
                      <Input
                        size="small"
                        placeholder={t('End date')}
                        fullWidth
                        readOnly
                        value={formattedDateTo ?? ''}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <Button
                      variant="text"
                      color="secondary"
                      onClick={handleReset}
                      disabled={!formattedDateFrom}
                    >
                      {t('Reset')}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        handleDateApply(
                          formattedDateFrom && formattedDateTo
                            ? `${formattedDateFrom} - ${formattedDateTo}`
                            : '',
                        );
                      }}
                      disabled={!formattedDateFrom}
                    >
                      {t('Apply')}
                    </Button>
                  </Box>
                </Box>
              }
            />
          </StyledDayPickerWrapper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default DatePickerRange;

const DropdownSelect = (dropdownProps: DropdownProps) => {
  const monthsInYear = 12;
  const isMonthDropdown = dropdownProps.options?.length === monthsInYear;
  const isSecondSelect = dropdownProps.displayIndex === 1;
  const options = isMonthDropdown
    ? dropdownProps.options
    : dropdownProps.options?.toReversed();
  const formattedOptions = useMemo(() => {
    return options?.map((option) => ({
      value: option.value,
      label: option.label.charAt(0).toUpperCase() + option.label.slice(1),
    }));
  }, [options]);

  const handleDropdownChange = (e: any) => {
    /** this logic helps us so that user can choose every month in every Select */
    if (isMonthDropdown && isSecondSelect) {
      dropdownProps.onChange?.({
        ...e,
        target: {
          ...e.target,
          value: e.target.value - 1,
        },
      });
    } else {
      dropdownProps.onChange?.(e);
    }
  };
  return (
    <Select
      value={dropdownProps.value as ValueTypes}
      onChangeNative={handleDropdownChange}
      fullWidth
      size="small"
      options={formattedOptions ?? []}
      selectedMenuVariant
    />
  );
};

const DropdownNavigation = (dropdownNavProps: DropdownNavProps) => {
  const ctx = useDayPicker();
  const data = Children.toArray(dropdownNavProps.children);
  const isFirstMonth =
    typeof data[0] === 'object' &&
    'props' in data[0] &&
    // @ts-expect-error TODO: fix this hacky typing
    getMonth(ctx.months[0].date) === (data[0].props.value as number);

  return (
    <Box
      sx={{
        padding: isFirstMonth ? '0 0 0 40px' : '0 40px 0 0',
      }}
      className="rdp-dropdowns"
    >
      {dropdownNavProps.children}
    </Box>
  );
};
